import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const inp = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-mono uppercase outline-none focus:border-slate-900";

export default function Locations() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ zone: "", aisle: "", shelf: "", bin: "", code: "" });
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/inventory/locations").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const preview = f.code.trim().toUpperCase() || [f.zone, f.aisle, f.shelf, f.bin].map((x) => x.trim().toUpperCase()).filter(Boolean).join("-");

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/inventory/locations", f);
      toast.success(`Location ${data.code} created`);
      setF({ zone: "", aisle: "", shelf: "", bin: "", code: "" });
      load();
    } catch (ex) {
      setError(errMsg(ex));
    }
  };
  const toggle = async (l, active) => {
    try {
      await api.put(`/inventory/locations/${l.id}`, { active });
      load();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <form onSubmit={create} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="location-form">
        <div className="font-display text-lg font-bold">New location</div>
        <div className="grid grid-cols-4 gap-2">
          {["zone", "aisle", "shelf", "bin"].map((k) => (
            <label key={k} className="space-y-1"><span className="text-[11px] font-bold uppercase text-slate-500">{k}</span>
              <input className={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value, code: "" })} data-testid={`location-${k}`} /></label>
          ))}
        </div>
        <div className="text-center font-mono text-[11px] uppercase tracking-widest text-slate-400">or free-text code</div>
        <input className={inp} value={f.code} placeholder="e.g. COLD-1" onChange={(e) => setF({ zone: "", aisle: "", shelf: "", bin: "", code: e.target.value })} data-testid="location-code" />
        <div className="rounded-lg bg-slate-950 px-3 py-2 font-mono text-sm text-amber-400" data-testid="location-preview">{preview || "—"}</div>
        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700" data-testid="location-error">{error}</div>}
        <button className="h-11 w-full rounded-xl bg-slate-900 font-bold text-white" data-testid="location-create">Create location</button>
      </form>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="location-list">
        {rows.map((l) => (
          <div key={l.id} className="flex items-center gap-3 px-4 py-3" data-testid={`location-row-${l.code}`}>
            <span className={`rounded-md px-2 py-1 font-mono text-sm font-bold ${l.kind === "system" ? "bg-amber-100 text-amber-900" : "bg-slate-900 text-white"}`}>{l.code}</span>
            <span className="text-xs text-slate-500">{l.kind === "system" ? "Built-in" : l.kind === "free" ? "Free text" : "Zone · Aisle · Shelf · Bin"}</span>
            <span className="ml-auto font-mono text-sm" data-testid={`location-qty-${l.code}`}>{l.qty} units</span>
            {l.kind !== "system" && <Switch checked={l.active} onCheckedChange={(v) => toggle(l, v)} data-testid={`location-active-${l.code}`} aria-label="Active" />}
          </div>
        ))}
      </div>
    </div>
  );
}
